import {
  BIG_JOKER, HEART, SMALL_JOKER, isWildcard, rankLabel, rankOf, suitOf,
  type Card as CardId, type Rank,
} from "@guandan/engine";
import "./card.css";

/**
 * Procedural card faces.
 *
 * Drawn rather than loaded so the game is playable before any art exists, and
 * so the two ranks that carry special meaning can be marked plainly:
 *
 *   the level card  — beats an ace, and newcomers keep forgetting
 *   the wildcard    — the heart of the level rank, which stands in for anything
 *
 * Both are the rules most often missed at a first table, so they get a visible
 * treatment rather than being left for the player to remember.
 */

const SUIT_GLYPH = ["♦", "♣", "♥", "♠"] as const;
const SUIT_RED = [true, false, true, false] as const;

export function shouldActivateCardClick(
  mode: "direct" | "parent",
  clickDetail: number,
): boolean {
  return mode === "direct" || clickDetail === 0;
}

export interface CardFaceProps {
  card: CardId;
  level: Rank;
  width: number;
  height: number;
  selected?: boolean;
  dimmed?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
  /** Marks a card the Hint button is pointing at. */
  hinted?: boolean;
  /** Let an overlapping hand own pointer selection while retaining keyboard activation. */
  pointerActivation?: "direct" | "parent";
  /** Index used by an overlapping hand to preserve the browser's visual hit target. */
  handIndex?: number;
}

export function CardFace({
  card, level, width, height, selected = false, dimmed = false, hinted = false,
  pointerActivation = "direct", handIndex, onClick, style,
}: CardFaceProps) {
  const rank = rankOf(card);
  const suit = suitOf(card);
  const joker = rank >= SMALL_JOKER;
  const wild = isWildcard(card, level);
  const isLevel = rank === level && !joker;
  const red = joker ? rank === BIG_JOKER : SUIT_RED[suit];

  const classes = [
    "card",
    red ? "card-red" : "card-black",
    selected ? "card-selected" : "",
    dimmed ? "card-dimmed" : "",
    wild ? "card-wild" : "",
    isLevel && !wild ? "card-level" : "",
    hinted ? "card-hinted" : "",
    onClick !== undefined ? "card-playable" : "",
  ].filter(Boolean).join(" ");

  const label = joker
    ? (rank === BIG_JOKER ? "Big joker" : "Small joker")
    : `${rankLabel(rank)} of ${["diamonds", "clubs", "hearts", "spades"][suit]}`;

  return (
    <div
      className={classes}
      data-hand-index={handIndex}
      style={{ width, height, fontSize: width, ...style }}
      onClick={onClick === undefined ? undefined : (event) => {
        if (shouldActivateCardClick(pointerActivation, event.detail)) onClick();
      }}
      role={onClick === undefined ? "img" : "button"}
      tabIndex={onClick === undefined ? undefined : 0}
      aria-label={wild ? `${label} — wildcard` : isLevel ? `${label} — level card` : label}
      aria-pressed={onClick === undefined ? undefined : selected}
      onKeyDown={onClick === undefined ? undefined : (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
      }}
    >
      {joker ? (
        <>
          <span className={`card-joker-corner card-joker-corner-tl ${rank === BIG_JOKER ? "joker-big" : "joker-small"}`} aria-hidden="true">
            <b>{rank === BIG_JOKER ? "BIG" : "SMALL"}</b><i>{rank === BIG_JOKER ? "★" : "✦"}</i>
          </span>
          <span className={`card-centre card-joker-centre ${rank === BIG_JOKER ? "joker-big" : "joker-small"}`} aria-hidden="true">
            <span>{rank === BIG_JOKER ? "★" : "✦"}</span><small>{rank === BIG_JOKER ? "BIG JOKER" : "SMALL JOKER"}</small>
          </span>
          <span className={`card-joker-corner card-joker-corner-br ${rank === BIG_JOKER ? "joker-big" : "joker-small"}`} aria-hidden="true">
            <b>{rank === BIG_JOKER ? "BIG" : "SMALL"}</b><i>{rank === BIG_JOKER ? "★" : "✦"}</i>
          </span>
        </>
      ) : (
        <>
          <span className="card-corner card-corner-tl"><span className="card-rank">{rankLabel(rank)}</span><span className="card-suit">{SUIT_GLYPH[suit]}</span></span>
          <span className="card-centre" aria-hidden="true">{SUIT_GLYPH[suit]}</span>
          <span className="card-corner card-corner-br"><span className="card-rank">{rankLabel(rank)}</span><span className="card-suit">{SUIT_GLYPH[suit]}</span></span>
        </>
      )}

      {wild && <span className="card-badge" title="Wildcard — stands in for any card">✦</span>}
      {isLevel && !wild && <span className="card-badge card-badge-level" title="Level card">●</span>}
    </div>
  );
}

/** Face-down card, for other players' hands and the deal. */
export function CardBack({ width, height, style }: { width: number; height: number; style?: React.CSSProperties }) {
  return <div className="card card-back" style={{ width, height, ...style }} aria-hidden="true" />;
}

/** A small stack standing in for a seat's remaining cards. */
export function CardStack({ count, width, height }: { count: number; width: number; height: number }) {
  const shown = Math.min(3, count);
  return (
    <div className="card-stack" style={{ width, height }} aria-label={`${count} cards`}>
      {Array.from({ length: shown }, (_, i) => (
        <CardBack
          key={i} width={width} height={height}
          style={{ transform: `translate(${i * 2}px, ${i * -2}px)`, zIndex: i }}
        />
      ))}
      <span className="card-stack-count tabular">{count}</span>
    </div>
  );
}

export { HEART };
